import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Empleados } from "./pages/Empleados";
import { Cortes } from "./pages/Cortes";
import { Maquila } from "./pages/Maquila";
import { Lavanderia } from "./pages/Lavanderia";
import { Habilitacion } from "./pages/Habilitacion";
import { Cierres } from "./pages/Cierres";
import { Proveedores } from "./pages/Proveedores";
import { Lotes } from "./pages/Lotes";
import { Telas } from "./pages/Telas";
import { Catalogos } from "./pages/Catalogos";
import { Asistencia } from "./pages/Asistencia";
import { Reportes } from "./pages/Reportes";
import { CuentasPorPagar } from "./pages/CuentasPorPagar";
import { Productos } from "./pages/Productos";
import { Pedidos } from "./pages/Pedidos";
import { Playeras } from "./pages/Playeras";
import { Usuarios } from "./pages/Usuarios";
import { Actividad } from "./pages/Actividad";
import { ProductoTerminado } from "./pages/ProductoTerminado";
import { PuntoVenta } from "./pages/PuntoVenta";
import { Botones } from "./pages/Botones";
import { Bolsas } from "./pages/Bolsas";
import { Cintas } from "./pages/Cintas";
import { Nomina } from "./pages/Nomina";
import { Clientes } from "./pages/Clientes";
import { Tienda } from "./pages/public/Tienda";
import { PedidoTracking } from "./pages/public/PedidoTracking";
import { ProductoDetalle } from "./pages/public/ProductoDetalle";
import { LimpiarCache } from "./pages/public/LimpiarCache";
import { Apariencia } from "./pages/Apariencia";
import { Toaster } from "./components/ui/sonner";
import { OfflineIndicator } from "./components/OfflineIndicator";

// Mapa de permiso → ruta (para redirigir a la primer pagina con permiso si no tiene la solicitada)
const PERMISO_TO_PATH = {
    dashboard: '/dashboard', lotes: '/lotes', cortes: '/cortes', maquila: '/maquila',
    lavanderia: '/lavanderia', habilitacion: '/habilitacion', telas: '/telas',
    cierres: '/cierres', playeras: '/playeras', proveedores: '/proveedores',
    empleados: '/empleados', asistencia: '/asistencia', catalogos: '/catalogos',
    reportes: '/reportes', cuentas_por_pagar: '/cuentas-por-pagar',
    productos: '/admin/productos', pedidos: '/admin/pedidos',
    producto_terminado: '/producto-terminado', ventas: '/punto-venta',
    botones: '/botones', bolsas: '/bolsas', cintas: '/cintas',
    nomina: '/nomina', clientes: '/clientes'
};

const ProtectedRoute = ({ children, permiso }) => {
    const { isAuthenticated, loading, hasPermission, user } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0C0C0E]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (permiso && !hasPermission(permiso)) {
        // Anti-loop: si no tiene el permiso solicitado, busca el primer permiso que SI tenga
        // y redirige a esa ruta. Si no tiene ninguno (caso raro), va a /login.
        const permisos = user?.permisos || [];
        const fallback = permisos.map(p => PERMISO_TO_PATH[p]).find(Boolean);
        if (!fallback || fallback === window.location.pathname) {
            return <Navigate to="/login" replace />;
        }
        return <Navigate to={fallback} replace />;
    }
    return <Layout>{children}</Layout>;
};

const AdminRoute = ({ children }) => {
    const { isAuthenticated, loading, isAdmin } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0C0C0E]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!isAdmin) return <Navigate to="/dashboard" replace />;
    return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0C0C0E]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;
    if (isAuthenticated) return <Navigate to="/dashboard" replace />;
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Store */}
            <Route path="/tienda" element={<Tienda />} />
            <Route path="/producto/:id" element={<ProductoDetalle />} />
            <Route path="/pedido/:codigo" element={<PedidoTracking />} />
            <Route path="/pedido" element={<PedidoTracking />} />
            <Route path="/limpiar-cache" element={<LimpiarCache />} />
            
            {/* Auth */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            
            {/* Permission-protected routes */}
            <Route path="/dashboard" element={<ProtectedRoute permiso="dashboard"><Dashboard /></ProtectedRoute>} />
            <Route path="/lotes" element={<ProtectedRoute permiso="lotes"><Lotes /></ProtectedRoute>} />
            <Route path="/telas" element={<ProtectedRoute permiso="telas"><Telas /></ProtectedRoute>} />
            <Route path="/cortes" element={<ProtectedRoute permiso="cortes"><Cortes /></ProtectedRoute>} />
            <Route path="/maquila" element={<ProtectedRoute permiso="maquila"><Maquila /></ProtectedRoute>} />
            <Route path="/lavanderia" element={<ProtectedRoute permiso="lavanderia"><Lavanderia /></ProtectedRoute>} />
            <Route path="/habilitacion" element={<ProtectedRoute permiso="habilitacion"><Habilitacion /></ProtectedRoute>} />
            <Route path="/cierres" element={<ProtectedRoute permiso="cierres"><Cierres /></ProtectedRoute>} />
            <Route path="/playeras" element={<ProtectedRoute permiso="playeras"><Playeras /></ProtectedRoute>} />
            <Route path="/proveedores" element={<ProtectedRoute permiso="proveedores"><Proveedores /></ProtectedRoute>} />
            <Route path="/empleados" element={<ProtectedRoute permiso="empleados"><Empleados /></ProtectedRoute>} />
            <Route path="/asistencia" element={<ProtectedRoute permiso="asistencia"><Asistencia /></ProtectedRoute>} />
            <Route path="/catalogos" element={<ProtectedRoute permiso="catalogos"><Catalogos /></ProtectedRoute>} />
            <Route path="/reportes" element={<ProtectedRoute permiso="reportes"><Reportes /></ProtectedRoute>} />
            <Route path="/cuentas-por-pagar" element={<ProtectedRoute permiso="cuentas_por_pagar"><CuentasPorPagar /></ProtectedRoute>} />
            <Route path="/admin/productos" element={<ProtectedRoute permiso="productos"><Productos /></ProtectedRoute>} />
            <Route path="/admin/pedidos" element={<ProtectedRoute permiso="pedidos"><Pedidos /></ProtectedRoute>} />
            <Route path="/producto-terminado" element={<ProtectedRoute permiso="producto_terminado"><ProductoTerminado /></ProtectedRoute>} />
            <Route path="/punto-venta" element={<ProtectedRoute permiso="ventas"><PuntoVenta /></ProtectedRoute>} />
            <Route path="/botones" element={<ProtectedRoute permiso="botones"><Botones /></ProtectedRoute>} />
            <Route path="/bolsas" element={<ProtectedRoute permiso="bolsas"><Bolsas /></ProtectedRoute>} />
            <Route path="/cintas" element={<ProtectedRoute permiso="cintas"><Cintas /></ProtectedRoute>} />
            <Route path="/nomina" element={<ProtectedRoute permiso="nomina"><Nomina /></ProtectedRoute>} />
            <Route path="/clientes" element={<ProtectedRoute permiso="clientes"><Clientes /></ProtectedRoute>} />
            
            <Route path="/apariencia" element={<ProtectedRoute permiso="productos"><Apariencia /></ProtectedRoute>} />
            
            {/* Admin-only routes */}
            <Route path="/usuarios" element={<AdminRoute><Usuarios /></AdminRoute>} />
            <Route path="/actividad" element={<AdminRoute><Actividad /></AdminRoute>} />
            
            <Route path="/" element={<Navigate to="/tienda" replace />} />
            <Route path="*" element={<Navigate to="/tienda" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                    <Toaster position="top-right" richColors />
                    <OfflineIndicator />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
